import type { ComponentProps, ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useFieldControl } from './Field';

/** Shared look of text fields and selects (44 px, 10 px radius, red border when invalid). */
export const controlClasses = cn(
  'h-11 w-full min-w-0 rounded-control border border-line-strong bg-surface px-3.5 text-body text-ink',
  'placeholder:text-muted/70 transition-[border-color,box-shadow] outline-none',
  'focus:border-primary focus:ring-3 focus:ring-primary/15',
  'aria-invalid:border-2 aria-invalid:border-danger aria-invalid:focus:ring-danger/15',
  'disabled:cursor-not-allowed disabled:bg-ground disabled:text-ink-2',
);

function useControlProps(props: {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: unknown;
}) {
  const field = useFieldControl();
  return {
    id: props.id ?? field?.id,
    'aria-describedby': props['aria-describedby'] ?? field?.describedBy,
    'aria-invalid': (props['aria-invalid'] as boolean | undefined) ?? (field?.invalid || undefined),
  };
}

export function Input({ className, ...props }: ComponentProps<'input'>) {
  return <input className={cn(controlClasses, className)} {...props} {...useControlProps(props)} />;
}

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return (
    <textarea
      className={cn(controlClasses, 'h-auto min-h-24 py-2.5', className)}
      {...props}
      {...useControlProps(props)}
    />
  );
}

export function Select({ className, children, ...props }: ComponentProps<'select'>) {
  return (
    <div className="relative min-w-0">
      <select
        className={cn(controlClasses, 'appearance-none pr-10', className)}
        {...props}
        {...useControlProps(props)}
      >
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-3 size-[18px] -translate-y-1/2 text-ink-2"
      />
    </div>
  );
}

/** A number or text field with a unit next to it ("100.000 | TND", "90 | days"). */
export function InputWithAddon({
  addon,
  className,
  inputClassName,
  ...props
}: ComponentProps<'input'> & { addon: ReactNode; inputClassName?: string }) {
  const control = useControlProps(props);
  return (
    <div
      className={cn(
        'flex h-11 min-w-0 overflow-hidden rounded-control border border-line-strong bg-surface',
        'focus-within:border-primary focus-within:ring-3 focus-within:ring-primary/15',
        control['aria-invalid'] && 'border-2 border-danger',
        props.disabled && 'bg-ground',
        className,
      )}
    >
      <input
        className={cn(
          'tabular w-full min-w-0 bg-transparent px-3.5 text-right text-body outline-none disabled:cursor-not-allowed disabled:text-ink-2',
          inputClassName,
        )}
        {...props}
        {...control}
      />
      <span className="flex items-center border-l border-line bg-ground px-3.5 text-ink-2">
        {addon}
      </span>
    </div>
  );
}
